import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';

import calibrationService from './calibrationService';

import type { SaveCalibrationAdjustmentParams } from './calibrationService';
import type {
    CalibrationAdjustment,
    CalibrationBulkAdjustmentRequest,
    CalibrationBulkAdjustmentResponse,
    CalibrationCycle,
    CalibrationPublishResponse,
    CalibrationReport,
    CalibrationReportFilters,
} from '@/types/calibration';

import { queryClient } from '@/AppWithProviders';
import { CalibrationQueryKey } from '@/constants/QueryKeys';

const invalidateCalibrationReport = (cycleEvaluationId: string) =>
    queryClient.invalidateQueries({ queryKey: [CalibrationQueryKey.REPORT, cycleEvaluationId] });

export const useGetCalibrationCycles = () => {
    return useQuery<CalibrationCycle[]>({
        queryKey: [CalibrationQueryKey.CYCLES],
        queryFn: () => calibrationService.getCycles(),
    });
};

export interface UseGetCalibrationReportParams {
    cycleEvaluationId: string | undefined;
    filters?: CalibrationReportFilters;
}

export const useGetCalibrationReport = ({
    cycleEvaluationId,
    filters,
}: UseGetCalibrationReportParams) => {
    return useQuery<CalibrationReport>({
        queryKey: [CalibrationQueryKey.REPORT, cycleEvaluationId, filters],
        // `enabled` guards this call: the query never runs while `cycleEvaluationId` is undefined.
        queryFn: () => calibrationService.getReport(cycleEvaluationId as string, filters),
        enabled: !!cycleEvaluationId,
        // Keeps the previous cycle's charts on screen while a filter change refetches, instead of
        // flashing them empty.
        placeholderData: keepPreviousData,
    });
};

export type SaveCalibrationAdjustmentVariables = Omit<
    SaveCalibrationAdjustmentParams,
    'cycleEvaluationId'
>;

export const useSaveCalibrationAdjustment = (cycleEvaluationId: string) => {
    return useMutation<CalibrationAdjustment, Error, SaveCalibrationAdjustmentVariables>({
        mutationFn: ({ employeeId, request }) =>
            calibrationService.saveAdjustment({ cycleEvaluationId, employeeId, request }),
        onSuccess: () => invalidateCalibrationReport(cycleEvaluationId),
    });
};

export const useBulkAdjustCalibration = (cycleEvaluationId: string) => {
    return useMutation<CalibrationBulkAdjustmentResponse, Error, CalibrationBulkAdjustmentRequest>({
        mutationFn: (request) => calibrationService.bulkAdjust(cycleEvaluationId, request),
        onSuccess: () => invalidateCalibrationReport(cycleEvaluationId),
    });
};

export const usePublishCalibrationAdjustments = (cycleEvaluationId: string) => {
    return useMutation<CalibrationPublishResponse, Error, void>({
        mutationFn: () => calibrationService.publishAdjustments(cycleEvaluationId),
        onSuccess: () => invalidateCalibrationReport(cycleEvaluationId),
    });
};
